import React from "react";
import { diffLines, diffWordsWithSpace } from "diff";

const MAX_CTX = 3;

/**
 * Shows a side-by-side line/word-level diff between priorPost and post.
 */
const BlogDiffPanel = ({ priorPost, post }) => {
  const lineChunks = diffLines(priorPost.content || "", post.content || "");
  const hasChanges = lineChunks.some(c => c.added || c.removed);

  const renderContentDiff = () => {
    if (!hasChanges) {
      return <div className="px-3 py-2 text-gray-400 italic">No content changes.</div>;
    }

    const result = [];
    let ci = 0;

    while (ci < lineChunks.length) {
      const chunk = lineChunks[ci];

      if (!chunk.added && !chunk.removed) {
        const lines = chunk.value.split("\n");
        if (lines[lines.length - 1] === "") lines.pop();

        if (lines.length > MAX_CTX * 2 + 1) {
          lines.slice(0, MAX_CTX).forEach((l, j) => {
            result.push(<div key={`${ci}h${j}`} className="px-3 py-0.5 text-gray-400 whitespace-pre-wrap">{l || "\u00a0"}</div>);
          });
          result.push(
            <div key={`${ci}mid`} className="px-3 py-1 text-gray-300 bg-gray-50 text-center border-y border-gray-100 select-none">
              ·· {lines.length - MAX_CTX * 2} unchanged lines ··
            </div>
          );
          lines.slice(-MAX_CTX).forEach((l, j) => {
            result.push(<div key={`${ci}t${j}`} className="px-3 py-0.5 text-gray-400 whitespace-pre-wrap">{l || "\u00a0"}</div>);
          });
        } else {
          lines.forEach((l, j) => {
            result.push(<div key={`${ci}-${j}`} className="px-3 py-0.5 text-gray-400 whitespace-pre-wrap">{l || "\u00a0"}</div>);
          });
        }
        ci++;
      } else if (chunk.removed) {
        const nextChunk = lineChunks[ci + 1];
        if (nextChunk && nextChunk.added) {
          // Replacement — word-level diff of the pair
          const wordChunks = diffWordsWithSpace(chunk.value, nextChunk.value);
          result.push(
            <div key={`${ci}-pair`} className="px-3 py-1.5 bg-yellow-50 border-y border-yellow-100 whitespace-pre-wrap leading-relaxed">
              {wordChunks.map((wc, j) => {
                if (wc.added)   return <span key={j} className="bg-green-200 text-green-900 rounded">{wc.value}</span>;
                if (wc.removed) return <span key={j} className="bg-red-200 text-red-800 line-through rounded">{wc.value}</span>;
                return <span key={j} className="text-gray-600">{wc.value}</span>;
              })}
            </div>
          );
          ci += 2;
        } else {
          const lines = chunk.value.split("\n");
          if (lines[lines.length - 1] === "") lines.pop();
          lines.forEach((l, j) => {
            result.push(
              <div key={`${ci}-${j}`} className="px-3 py-0.5 bg-red-50 text-red-700 line-through whitespace-pre-wrap">
                <span className="select-none opacity-40 mr-1">−</span>{l}
              </div>
            );
          });
          ci++;
        }
      } else {
        // Standalone added lines
        const lines = chunk.value.split("\n");
        if (lines[lines.length - 1] === "") lines.pop();
        lines.forEach((l, j) => {
          result.push(
            <div key={`${ci}-${j}`} className="px-3 py-0.5 bg-green-50 text-green-800 whitespace-pre-wrap">
              <span className="select-none opacity-40 mr-1">+</span>{l}
            </div>
          );
        });
        ci++;
      }
    }
    return result;
  };

  return (
    <div className="max-w-[860px] mx-auto px-4 py-6">
      <div className="bg-white border border-sky-200 rounded-xl shadow-sm overflow-hidden">
        <div className="px-5 py-3 bg-sky-50 border-b border-sky-200 flex items-center gap-2">
          <span className="text-sky-700 font-semibold text-sm">📋 Changes from previous version</span>
          <span className="ml-auto text-xs text-sky-400">Green = added · Red = removed</span>
        </div>
        <div className="p-5 space-y-5">

          {/* Title diff */}
          {priorPost.title !== post.title && (
            <div>
              <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-1">Title</p>
              <div className="text-sm rounded overflow-hidden border border-gray-200">
                <div className="bg-red-50 text-red-800 px-3 py-1.5 line-through font-mono leading-relaxed">{priorPost.title}</div>
                <div className="bg-green-50 text-green-800 px-3 py-1.5 font-mono leading-relaxed">{post.title}</div>
              </div>
            </div>
          )}

          {/* Description diff */}
          {priorPost.description !== post.description && (
            <div>
              <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-1">Description</p>
              <div className="text-sm rounded overflow-hidden border border-gray-200">
                <div className="bg-red-50 text-red-800 px-3 py-1.5 line-through font-mono leading-relaxed">{priorPost.description}</div>
                <div className="bg-green-50 text-green-800 px-3 py-1.5 font-mono leading-relaxed">{post.description}</div>
              </div>
            </div>
          )}

          {/* Content diff */}
          <div>
            <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-1">Content</p>
            <div className="text-xs font-mono rounded border border-gray-200 overflow-auto max-h-[600px] bg-white">
              {renderContentDiff()}
            </div>
          </div>

        </div>
      </div>
    </div>
  );
};

export default BlogDiffPanel;
